import React from "react";
import { Button, Input, Select } from "antd";
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import {
  SOCIAL_NETWORKS,
  SocialLink,
  SocialNetwork,
  createSocial,
  nextSocialNetwork,
} from "../../../blocks";

import { LinkField } from "../../Fields";

import styles from "./index.module.scss";

interface SocialRowsProps {
  value: SocialLink[];
  onChange: (rows: SocialLink[]) => void;
}

interface RowProps {
  row: SocialLink;
  onPatch: (patch: Partial<SocialLink>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function Row({ row, onPatch, onRemove, canRemove }: RowProps) {
  const { t } = useTranslation();
  const { setNodeRef, listeners, attributes, transform, transition } =
    useSortable({ id: row.id });

  return (
    <div
      ref={setNodeRef}
      className={styles.row}
      // Written out rather than taken from @dnd-kit/utilities: that package is
      // only in node_modules as a dependency of core and sortable, not one this
      // project declares, and a row moving under the pointer needs no more than
      // its own offset.
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
      }}
    >
      <Button
        type="text"
        className={styles.handle}
        icon={<HolderOutlined />}
        aria-label={t("pages.campaigns.builder.drag")}
        {...attributes}
        {...listeners}
      />

      <Select<SocialNetwork>
        className={styles.network}
        value={row.network}
        onChange={(network) => onPatch({ network })}
        options={SOCIAL_NETWORKS.map((network) => ({
          value: network,
          label: t(`pages.campaigns.networks.${network}`),
        }))}
      />

      <Button
        type="text"
        danger
        disabled={!canRemove}
        icon={<DeleteOutlined />}
        aria-label={t("pages.campaigns.builder.remove")}
        onClick={onRemove}
      />

      <LinkField
        className={styles.url}
        value={row.url}
        onChange={(url) => onPatch({ url })}
      />

      <Input
        className={styles.alt}
        value={row.alt}
        placeholder={t("pages.campaigns.builder.alt")}
        onChange={(event) => onPatch({ alt: event.target.value })}
      />
    </div>
  );
}

/**
 * The links a social section shows: which network, where it goes, and what is
 * read in place of the icon. The order here is the order in the letter, and it
 * is changed by dragging rather than by a pair of arrows.
 */
export default function SocialRows({ value, onChange }: SocialRowsProps) {
  const { t } = useTranslation();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = value.findIndex((row) => row.id === active.id);
    const to = value.findIndex((row) => row.id === over.id);
    const next = [...value];
    const [moved] = next.splice(from, 1);

    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className={styles.rows}>
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={value.map((row) => row.id)}
          strategy={verticalListSortingStrategy}
        >
          {value.map((row) => (
            <Row
              key={row.id}
              row={row}
              canRemove={value.length > 1}
              onPatch={(patch) =>
                onChange(
                  value.map((item) =>
                    item.id === row.id ? { ...item, ...patch } : item,
                  ),
                )
              }
              onRemove={() =>
                onChange(value.filter((item) => item.id !== row.id))
              }
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        block
        icon={<PlusOutlined />}
        // The next network the section is not already showing, so a row is
        // usually right as it lands.
        onClick={() =>
          onChange([...value, createSocial(nextSocialNetwork(value))])
        }
      >
        {t("pages.campaigns.builder.addSocial")}
      </Button>
    </div>
  );
}
